<!--
/**
 * Port of apps/admin/src/components/Subscription.vue (Phase 2.3).
 *
 * Global, self-contained dialog mounted by App.vue for NON-sr/me/ba venues
 * (Paddle-billed tenants). Entirely inert unless VITE_ENABLE_BILLING === 'true'
 * (old: VUE_APP_ENABLE_BILLING) — the gate skips listener registration exactly
 * like the old created() early-return.
 *
 * Channels (old EventBus → useAppEvents, same names/payloads):
 * - 'notification-received': subscription-created/updated → auto-hide after 4s;
 *   subscription-canceled/paused → reload tenant (GET /me → TenantService.setTenant)
 *   and show the dialog.
 *
 * Paddle (window.Paddle, typed in src/types/global.d.ts; loaded from CDN +
 * Paddle.Setup in main.ts behind the same billing flag):
 * - checkout(): Paddle.Checkout.open with VITE_PADDLE_PRODUCT_ID (old:
 *   VUE_APP_PADDLE_PRODUCT_ID), tenant email/country, tenant_id passthrough.
 * - updatePaymentDetails(): Paddle.Checkout.open with the subscription's
 *   p_update_url override.
 *
 * API calls:
 * - POST /subscription/update { pause: false } — restartSubscription (pay/activate).
 * - GET /me → TenantService.setTenant(response.data) — reloadTenant.
 *
 * Parity notes:
 * - Old dialog was fullscreen on xs (`:fullscreen="$vuetify.breakpoint.xs"`) →
 *   KModal's default fullscreen-enabled (fullscreen on xs) matches.
 * - Old #59b200 (brand green) buttons/alerts → semantic `success` (green →
 *   success, a hue divergence). The old unpaid alert was
 *   `type="info" color="#59b200"` — in Vuetify the color prop wins, so it rendered
 *   green with an info icon; the type→icon mapping (ex-KAlert) renders green with
 *   a check icon instead (icon-glyph divergence, visual only).
 *   Old xs scroll-y-reverse transition → UModal default transition (visual only).
 *   Old overlay #2c2c38/0.8 → default overlay.
 */
-->
<template>
    <UModal :open="visible" :dismissible="false" :close="false" :ui="{ content: 'sm:max-w-125' }">
        <template #content>
            <div v-if="!paid">
                <!-- Old: v-card-title.text-h6.justify-center -->
                <div class="flex justify-center px-6 pt-5 text-panel-title">
                    {{ t('subscription.heading') }}
                </div>

                <div class="p-6">
                    <!-- Old: type="info" color="#59b200" — color wins in Vuetify → green (§8.2: green → success) -->
                    <UAlert color="success" icon="i-lucide-circle-check" variant="soft">
                        <template #description>
                            <span v-if="subscriptionStatus === 'trial_expired'">{{ t('subscription.trial_expired') }}</span>
                            <span v-else-if="subscriptionStatus === 'expired_contact_support'">{{ t('subscription.expired_contact_support') }}</span>
                            <span v-else-if="subscriptionStatus === 'canceled'">{{ t('subscription.canceled') }}</span>
                            <span v-else-if="subscriptionStatus === 'payment_failed'">{{ t('subscription.payment_failed') }}</span>
                            <span v-else-if="subscriptionStatus === 'paused'">{{ t('subscription.paused') }}{{ nextBillDate }}</span>
                        </template>
                    </UAlert>

                    <div v-if="subscriptionStatus !== 'expired_contact_support'" class="mt-6 mb-1 flex justify-center">

                        <template v-if="subscriptionStatus === 'trial_expired' || subscriptionStatus === 'canceled'">
                            <UButton
                                color="success"
                                size="lg"
                                leading-icon="i-lucide-chevrons-right"
                                class="mr-3"
                                @click="checkout()"
                            >
                                {{ t('general.continue') }}
                            </UButton>

                            <UButton
                                color="success"
                                size="lg"
                                leading-icon="i-lucide-log-out"
                                @click="logoutTenant()"
                            >
                                {{ t('general.log_out') }}
                            </UButton>
                        </template>

                        <template v-if="subscriptionStatus === 'payment_failed'">
                            <UButton
                                color="neutral"
                                variant="outline"
                                size="lg"
                                class="mr-3"
                                @click="updatePaymentDetails()"
                            >
                                {{ t('subscription.update_payment_details') }}
                            </UButton>

                            <UButton
                                color="success"
                                size="lg"
                                class="w-[150px] justify-center"
                                @click="restartSubscription()"
                            >
                                {{ t('subscription.pay') }}
                            </UButton>
                        </template>

                        <UButton
                            v-if="subscriptionStatus === 'paused'"
                            color="success"
                            size="lg"
                            block
                            trailing-icon="i-lucide-chevrons-right"
                            @click="restartSubscription()"
                        >
                            {{ t('subscription.activate') }}
                        </UButton>

                    </div>
                </div>
            </div>

            <div v-else>
                <!-- Old: v-card-title.text-h6 (left-aligned on the paid card) -->
                <div class="px-6 pt-5 text-panel-title">
                    {{ t('general.payment_successful') }}
                </div>

                <div class="p-6">
                    <UAlert color="success" icon="i-lucide-circle-check" variant="soft">
                        <template #description>
                            {{ t('subscription.thank_you_continue') }}
                        </template>
                    </UAlert>
                </div>
            </div>
        </template>
    </UModal>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import moment from 'moment-timezone'
import { apiService, TenantService, type ITenant } from 'app-core'
import { useAppStore } from '@/stores/useAppStore'
import { useAppEvents, type AppEventPayloads } from '@/composables/useAppEvents'

const { t } = useI18n()
const router = useRouter()
const appEvents = useAppEvents()
const { tenant } = storeToRefs(useAppStore())

/**
 * Paddle billing fields the /me payload carries that ITenant doesn't declare
 * (verified against old admin's runtime usage in Subscription.vue — the old app
 * was untyped JS); typed locally so strict TS checks the same property paths.
 */
interface PaddleSubscription {
    p_status?: string | null
    p_paused_reason?: string | null
    p_next_bill_date?: string | null
    p_update_url?: string | null
}
type BillingTenant = ITenant & { subscription?: PaddleSubscription | null }

const billingTenant = computed(() => tenant.value as BillingTenant | null)

const visible = ref(false)
const paid = ref(false)

const subscriptionStatus = computed(() => {
    // Old code accessed this.tenant unconditionally — App.vue mounts Subscription
    // only when tenant?.venue exists, so the non-null assertions preserve the old
    // behavior (a missing tenant/venue would throw, as before).
    const tnt = billingTenant.value!

    if (!tnt.subscription) {
        return !tnt.venue!.settings.subscription_payments_enabled
            ? 'expired_contact_support'
            : 'trial_expired'
    } else if (tnt.subscription.p_status === 'deleted') {
        return 'canceled'
    } else if (tnt.subscription.p_paused_reason === 'delinquent') {
        return 'payment_failed'
    } else if (tnt.subscription.p_paused_reason === 'voluntary') {
        return 'paused'
    } else {
        return 'active'
    }
})

const nextBillDate = computed(() => {
    // Rendered only for the 'paused' status, where the subscription exists — the
    // old code dereferenced tenant.subscription unconditionally here too.
    const tnt = billingTenant.value!
    const date = moment(tnt.subscription!.p_next_bill_date)

    if (date.isBefore(moment().startOf('day').add(1, 'days'))) {
        return '.'
    }

    // parity: pre-existing venue-locale gating (docs/AdminFeature.md, ex-parity-spec §6)
    const dateString = ['me', 'sr', 'ba'].includes(tnt.venue!.locale)
        ? date.format('DD.MM.Y')
        : date.format('Y-MM-DD')

    return ' ' + t('subscription.on_date', { date: dateString })
})

// created() — old early-return: listeners are registered only when billing is on.
let offNotificationReceived: (() => void) | null = null

if (import.meta.env.VITE_ENABLE_BILLING === 'true') {
    offNotificationReceived = appEvents.on('notification-received', onNotificationReceived)
}

onBeforeUnmount(() => {
    offNotificationReceived?.()
})

async function onNotificationReceived(notification: AppEventPayloads['notification-received']) {
    if (notification.type === 'subscription-created' || notification.type === 'subscription-updated') {
        setTimeout(() => {
            hide()
        }, 4000)
    }

    if (notification.type === 'subscription-canceled' || notification.type === 'subscription-paused') {
        await reloadTenant()
        visible.value = true
        paid.value = false
    }
}

function checkout() {
    if (!window.Paddle) {
        return
    }

    visible.value = false

    window.Paddle.Checkout.open({
        product: import.meta.env.VITE_PADDLE_PRODUCT_ID,
        title: t('subscription.plan_title'),
        message: t('subscription.plan_message'),
        email: billingTenant.value!.email,
        country: billingTenant.value!.venue!.country,
        passthrough: JSON.stringify({ tenant_id: billingTenant.value!.id }),
        successCallback: successfullPurchase,
        closeCallback: failedPurchase
    })
}

function updatePaymentDetails() {
    if (!window.Paddle) {
        return
    }

    visible.value = false

    window.Paddle.Checkout.open({
        override: billingTenant.value!.subscription!.p_update_url,
        passthrough: JSON.stringify({ tenant_id: billingTenant.value!.id }),
        successCallback: () => {
            setTimeout(() => {
                visible.value = true
            }, 1000)
        },
        closeCallback: failedPurchase
    })
}

function restartSubscription() {
    apiService.post('/subscription/update', { pause: false })
        .then(() => {
            successfullPurchase()
        }).catch(error => {
            console.log(error)
        })
}

function successfullPurchase() {
    paid.value = true
    visible.value = true

    setTimeout(() => {
        hide()
        location.reload()
    }, 4000)
}

function failedPurchase() {
    paid.value = false
    visible.value = true
}

function hide() {
    visible.value = false
    setTimeout(() => {
        paid.value = false
    }, 500)
}

async function reloadTenant() {
    const response = await apiService.get<ITenant>('/me')
    TenantService.setTenant(response.data)
}

/**
 * Log out Tenant
 */
function logoutTenant() {
    TenantService.logout()
    router.push({ name: 'tenantLogin' }).catch(() => {})
}
</script>
